import type {
  ESignFormName,
  ESignFormStatusData,
  ESignFormStatusItem,
} from 'features/Procedure/state/procedure-api.type'

// The signable e-sign form each tab represents (drives the tab's lock icon, the
// floating eSign button, and its lock behavior). Treatments maps to the encounter
// (not the shared soap_r form): the SOAP note is signed from the Notes tab, while
// the Treatments tab signs / locks on the encounter itself.
export const TAB_TO_FORM_NAME: Record<string, ESignFormName> = {
  treatments: 'encounter',
  photos: 'photo',
  soap: 'soap_r',
  products: 'product',
  labs: 'labs',
}

// Tabs that lock on the encounter sign IN ADDITION to their own per-form sign flag.
// Notes (soap) locks when its own soap_r form is signed OR when the encounter is
// signed. Every other tab locks only on its own form; Treatments already maps to
// the encounter form above, so it locks on the encounter through that mapping.
const ENCOUNTER_LOCKED_TAB_KEYS: ReadonlySet<string> = new Set(['soap'])

export const hasESignItems = (status?: Pick<ESignFormStatusItem, 'items'> | null): boolean => {
  return Array.isArray(status?.items) && status.items.length > 0
}

export const getTabSignedStatus = (
  eSignStatus: ESignFormStatusData | undefined,
  tabKey: string,
): boolean => {
  if (!eSignStatus) return false
  const formName = TAB_TO_FORM_NAME[tabKey]
  if (!formName) return false
  const status = eSignStatus[formName]
  return status?.is_signed === 1
}

export const getTabHasFormToSign = (
  eSignStatus: ESignFormStatusData | undefined,
  tabKey: string,
): boolean => {
  if (!eSignStatus) return false
  const formName = TAB_TO_FORM_NAME[tabKey]
  if (!formName) return false
  const status = eSignStatus[formName]
  return status?.has_form_to_sign === 1
}

export const getTabHasESignItems = (
  eSignStatus: ESignFormStatusData | undefined,
  tabKey: string,
): boolean => {
  if (!eSignStatus) return false
  const formName = TAB_TO_FORM_NAME[tabKey]
  if (!formName) return false
  return hasESignItems(eSignStatus[formName])
}

export const getIsEncounterSigned = (eSignStatus: ESignFormStatusData | undefined): boolean =>
  eSignStatus?.encounter?.is_signed === 1

export const getTabActionsDisabled = (
  eSignStatus: ESignFormStatusData | undefined,
  tabKey: string,
  isInteractionLocked = false,
): boolean => {
  if (isInteractionLocked) return true
  if (getTabSignedStatus(eSignStatus, tabKey)) return true
  return ENCOUNTER_LOCKED_TAB_KEYS.has(tabKey) && getIsEncounterSigned(eSignStatus)
}
